import React from 'react';

import useLayout from '@/hooks/layout/useLayout';
import useOnScroll from '@/hooks/useOnScroll';
import reactUtils from '@/utils/reactUtils';
import {twx} from '@/utils/tailwindUtils';

import BackButton from '../BackButton/BackButton';

const StickyHeader = ({
    onBack,
    backButtonIcon,
    title,
    startIcon: startIconOrFunc,
    endIcon: endIconOrFunc,
    scrollElement = window,
    className,
    internalClassName,
    renderAs,
    isDark,
}) => {
    const {isMobile, isDesktop} = useLayout();
    const {opacity, isSticked} = useHeaderOpacity({scrollElement});

    // replace with useTheme hook during merge
    const {isThemeDark: systemIsThemeDark} = {};
    const isThemeDark = isDark ?? systemIsThemeDark;

    const parameters = {isSticked};
    const startIcon = reactUtils.getOrCreateComponent({
        component: startIconOrFunc,
        parameters,
    });
    const endIcon = reactUtils.getOrCreateComponent({
        component: endIconOrFunc,
        parameters,
    });
    const isStartElementVisible = isMobile || (isDesktop && (startIcon || onBack));
    const isEndElementVisible = isMobile || (isDesktop && endIcon);

    const ContainerComponent = renderAs || 'header';

    return (
        <ContainerComponent
            className={twx(
                'group fixed left-0 isolate z-30 flex w-full flex-col justify-center pb-4 pt-safe-offset-4 top-0-hw-offset',
                !isThemeDark && 'md:pb-8 md:pt-safe-offset-8',
                isThemeDark && 'text-romance',
                className
            )}
            data-is-sticked={isSticked}
        >
            <div
                style={{opacity}}
                className={twx(
                    'absolute left-0 top-0 h-full w-full border-b border-border bg-romance transition-opacity ease-out',
                    !isThemeDark && 'md:border-mischka',
                    isThemeDark && 'bg-primary-dark'
                )}
            />
            <div
                className={twx(
                    'relative z-10 flex h-8 items-center justify-between gap-4 px-4',
                    !isThemeDark && 'md:px-8 ml:justify-start ml:px-6',
                    !isThemeDark && endIcon && 'ml:pr-3',
                    internalClassName
                )}
            >
                {isStartElementVisible && (
                    <div className='relative w-16 ml:w-6'>
                        {startIcon ||
                            (onBack && (
                                <BackButton
                                    onClick={onBack}
                                    icon={backButtonIcon}
                                    className='absolute -left-3 top-1/2 -translate-y-1/2 ml:left-0'
                                />
                            ))}
                    </div>
                )}
                {title && (
                    <div
                        style={{opacity}}
                        className={twx(
                            'truncate text-center text-14 font-bold tracking-02 transition-opacity ease-out',
                            !isThemeDark && 'ml:flex-1 ml:text-left ml:text-24'
                        )}
                        aria-hidden={!isSticked}
                    >
                        {title}
                    </div>
                )}
                {isEndElementVisible && (
                    <div className={twx('relative flex w-16 justify-end', !isThemeDark && 'ml:w-12')}>{endIcon}</div>
                )}
            </div>
        </ContainerComponent>
    );
};

const SCROLL_TOP_FOR_VISIBLE_HEADER = 30;
const SCROLL_TOP_OFFSET = 10;
const useHeaderOpacity = ({scrollElement}) => {
    const scrollTop = useOnScroll({scrollElement});

    const scrolledValue = scrollTop - SCROLL_TOP_OFFSET;
    const opacity = scrolledValue > SCROLL_TOP_FOR_VISIBLE_HEADER ? 1 : scrolledValue / SCROLL_TOP_FOR_VISIBLE_HEADER;

    return {opacity, isSticked: opacity > 0};
};

export default StickyHeader;
